import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Checkbox,
  Input,
  message,
  Select,
  TableColumnsType,
  Tooltip,
} from 'antd';
import { Text } from '@quinyx/ui';
import { CheckOutlined, CloseOutlined, MinusOutlined } from '@ant-design/icons';

import Table from '../../../components/Table';
import { ICustomerPaginationContent } from '@/api/interfaces/customer';
import { usePaginatedCustomers } from '@/hooks/customer';
import {
  getAvailableModules,
  getModuleGroups,
  ModuleInfo,
  updateModulesBatch,
} from '@/api/endpoints/modules';
import { handleError } from '@/api/utils';
import { withCustomerLayout } from '@/layouts/customer';
import { useTranslate } from '@/hooks/translate';

const { Search } = Input;
const { Option } = Select;

const Modules = () => {
  const { t } = useTranslate();
  const [selectedCustomers, setSelectedCustomers] = useState<Set<number>>(
    new Set(),
  );
  const [selectedModule, setSelectedModule] = useState<string | undefined>(
    undefined,
  );
  const [availableModules, setAvailableModules] = useState<ModuleInfo[]>([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [customersWithModule, setCustomersWithModule] = useState<Set<number>>(
    new Set(),
  );
  const [isLoadingModuleGroups, setIsLoadingModuleGroups] = useState(false);
  const [moduleFilter, setModuleFilter] = useState<
    'all' | 'hasModule' | 'noModule'
  >('all');
  const { paginatedCustomers, isLoading, getPaginatedCustomers } =
    usePaginatedCustomers();

  useEffect(() => {
    const loadModules = async () => {
      try {
        const modules = await getAvailableModules();
        setAvailableModules(modules);
      } catch (error) {
        handleError(error);
      }
    };
    loadModules();
  }, []);

  useEffect(() => {
    const loadModuleGroups = async () => {
      if (!selectedModule) {
        setCustomersWithModule(new Set());
        setIsLoadingModuleGroups(false);
        return;
      }

      setIsLoadingModuleGroups(true);
      try {
        const customerIds = await getModuleGroups(selectedModule);
        setCustomersWithModule(new Set(customerIds));
      } catch (error) {
        handleError(error);
        setCustomersWithModule(new Set());
      } finally {
        setIsLoadingModuleGroups(false);
      }
    };
    loadModuleGroups();
  }, [selectedModule]);

  const handleModuleUpdate = async (isEnabled: boolean) => {
    if (!selectedModule || selectedCustomers.size === 0) return;

    setIsUpdating(true);
    try {
      const result = await updateModulesBatch(selectedModule, {
        isEnabled,
        customerIds: Array.from(selectedCustomers),
      });

      if (result.successCount > 0) {
        message.success(
          t('toast.moduleUpdateSuccess', {
            action: t(
              isEnabled ? 'toast.moduleEnabled' : 'toast.moduleDisabled',
            ),
            module: selectedModule,
            count: result.successCount,
          }),
        );
      }

      if (result.failureCount > 0) {
        message.warning(
          t('toast.moduleUpdatePartialFailure', {
            count: result.failureCount,
            errors: result.errors.join(', '),
          }),
        );
      }

      // Refresh module groups to update the "Has Module" column
      if (result.successCount > 0 && selectedModule) {
        setIsLoadingModuleGroups(true);
        try {
          const customerIds = await getModuleGroups(selectedModule);
          setCustomersWithModule(new Set(customerIds));
        } catch (error) {
          // Intentionally ignore errors from this best-effort refresh; the main update succeeded,
          // and the "Has Module" column will be corrected on the next full data load.
        } finally {
          setIsLoadingModuleGroups(false);
        }
      }

      // Keep customer selection after update
    } catch (error) {
      message.error(t('toast.moduleUpdateError'));
    } finally {
      setIsUpdating(false);
    }
  };

  const filteredCustomers = useMemo(() => {
    const customers = paginatedCustomers?.content || [];

    if (!selectedModule || moduleFilter === 'all' || isLoadingModuleGroups) {
      return customers;
    }

    return customers.filter((customer) => {
      const hasModule = customersWithModule.has(customer.domainId);
      if (moduleFilter === 'hasModule') {
        return hasModule;
      }
      if (moduleFilter === 'noModule') {
        return !hasModule;
      }
      return true;
    });
  }, [
    paginatedCustomers?.content,
    selectedModule,
    moduleFilter,
    customersWithModule,
    isLoadingModuleGroups,
  ]);

  const visibleCustomerIds = useMemo(() => {
    return filteredCustomers.map((c) => c.domainId);
  }, [filteredCustomers]);

  const allVisibleSelected = useMemo(() => {
    if (visibleCustomerIds.length === 0) return false;
    return visibleCustomerIds.every((id) => selectedCustomers.has(id));
  }, [visibleCustomerIds, selectedCustomers]);

  const someVisibleSelected = useMemo(() => {
    return visibleCustomerIds.some((id) => selectedCustomers.has(id));
  }, [visibleCustomerIds, selectedCustomers]);

  const handleSelectAll = (checked: boolean) => {
    const newSelected = new Set(selectedCustomers);
    if (checked) {
      visibleCustomerIds.forEach((id) => newSelected.add(id));
    } else {
      visibleCustomerIds.forEach((id) => newSelected.delete(id));
    }
    setSelectedCustomers(newSelected);
  };

  const columns: TableColumnsType<ICustomerPaginationContent> = useMemo(
    () => [
      {
        title: (
          <Checkbox
            checked={allVisibleSelected}
            indeterminate={someVisibleSelected && !allVisibleSelected}
            onChange={(e) => handleSelectAll(e.target.checked)}
          />
        ),
        key: 'select',
        width: 50,
        render: (_, customer) => (
          <Checkbox
            checked={selectedCustomers.has(customer.domainId)}
            onChange={(e) => {
              const newSelected = new Set(selectedCustomers);
              if (e.target.checked) {
                newSelected.add(customer.domainId);
              } else {
                newSelected.delete(customer.domainId);
              }
              setSelectedCustomers(newSelected);
            }}
          />
        ),
      },
      {
        title: t('customer.customers'),
        dataIndex: 'name',
        key: 'name',
      },
      {
        title: t('customer.domainId'),
        dataIndex: 'domainId',
        key: 'domainId',
      },
      {
        title: t('customer.hasModule'),
        key: 'hasModule',
        width: 100,
        render: (_, customer) => {
          if (!selectedModule || isLoadingModuleGroups) {
            return (
              <Tooltip title={t('customer.selectModuleToSeeStatus')}>
                <Text color="SurfaceOnSurfaceAlt">
                  <MinusOutlined />
                </Text>
              </Tooltip>
            );
          }

          const hasModule = customersWithModule.has(customer.domainId);
          return hasModule ? (
            <CheckOutlined style={{ color: '#52c41a' }} />
          ) : (
            <CloseOutlined style={{ color: '#ff4d4f' }} />
          );
        },
      },
      {
        title: t('customer.active'),
        dataIndex: 'active',
        key: 'active',
        render: (active) => (active ? t('common.yes') : t('common.no')),
      },
      {
        title: t('customer.createdDate'),
        dataIndex: 'dateCreated',
        key: 'dateCreated',
      },
    ],
    [
      t,
      allVisibleSelected,
      someVisibleSelected,
      selectedCustomers,
      selectedModule,
      isLoadingModuleGroups,
      customersWithModule,
    ],
  );

  return (
    <div>
      <Card title={t('customer.searchCustomers')} style={{ marginBottom: 16 }}>
        <Search
          placeholder={t('customer.searchByCustomerName')}
          allowClear
          onSearch={(value) => {
            getPaginatedCustomers({
              page: 1,
              name: value || undefined,
            });
          }}
          style={{ width: 300 }}
        />
      </Card>

      <Text color="SurfaceOnSurfaceAlt">
        {t('customer.bulkModuleInstructions')}
      </Text>

      <Table
        data-test-id="modules-customers-table"
        panel={() =>
          t('customer.allCustomers', {
            count: paginatedCustomers?.totalElements || 0,
          })
        }
        getRowKey={(customer) => customer.domainId}
        columns={columns}
        isLoading={isLoading}
        data={filteredCustomers}
        pagination={{
          total: paginatedCustomers?.totalElements || 0,
          current: (paginatedCustomers?.number || 0) + 1,
          pageSize: paginatedCustomers?.size || 10,
          showSizeChanger: true,
          onChange: (page, size) => {
            getPaginatedCustomers({
              page,
              size,
            });
          },
        }}
      />

      <Card title={t('customer.moduleActions')} style={{ marginTop: 16 }}>
        <div style={{ marginBottom: 16 }}>
          <Select
            placeholder={t('customer.selectModule')}
            style={{ width: 300 }}
            value={selectedModule}
            onChange={(value) => {
              setSelectedModule(value);
              setModuleFilter('all');
            }}
            allowClear>
            {availableModules.map((module) => (
              <Option key={module.id} value={module.id}>
                {module.id}
              </Option>
            ))}
          </Select>
        </div>

        {selectedModule && (
          <div style={{ marginBottom: 16 }}>
            <Select
              placeholder={t('customer.filterByModuleStatus')}
              style={{ width: 300 }}
              value={moduleFilter}
              onChange={setModuleFilter}>
              <Option value="all">{t('customer.allCustomersFilter')}</Option>
              <Option value="hasModule">{t('customer.hasModule')}</Option>
              <Option value="noModule">{t('customer.noModule')}</Option>
            </Select>
          </div>
        )}

        <Text color="SurfaceOnSurfaceAlt">
          {t('customer.customersSelected', { count: selectedCustomers.size })}
        </Text>

        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            type="primary"
            loading={isUpdating}
            disabled={selectedCustomers.size === 0 || !selectedModule}
            onClick={() => handleModuleUpdate(true)}>
            {t('customer.enableModule')}
          </Button>
          <Button
            loading={isUpdating}
            disabled={selectedCustomers.size === 0 || !selectedModule}
            onClick={() => handleModuleUpdate(false)}>
            {t('customer.disableModule')}
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default withCustomerLayout(Modules);
